/*
 * File: registry.ts
 * File Created: 2023-12-18 11:08:18
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-09-16 03:44:26
 * Modified By: Val Liu
 * -----
 */

import { fastJSONStableStringify } from "./fast-json-stable-stringify";

/// a generic registry for classes.
interface IRegistry<T> {
    registerClass(meta?: T): (constructor: new (...args: any[]) => any) => void;
    /// return the class which matches the name.
    getClass(name: string): IRegistryEntry<T> | null;
    /// return all classes which matches the meta object.
    getClass(meta?: T): IRegistryEntry<T>[] | null;
    /// return the cached class instance, which matches the name and arguments.
    getInstance(name: string, ...args: any): IRegistryInstance<T> | null;
    /// return all cached class instances, which matches the meta object and also the args.
    getInstance(meta?: T, ...args: any): IRegistryInstance<T>[] | null;
}

interface IRegistryEntry<T> {
    ctor: new (...args: any[]) => any;
    meta: T;
}

interface IRegistryInstance<T> extends IRegistryEntry<T> {
    instance: any;
}

export interface IRegistryMetaBase {
    /// alias for the UITag
    alias?: string | string[];
}

export class Registry {
    private static registryBox = new Map<symbol, Map<string, any>>();

    static create<T extends IRegistryMetaBase = any>(sym: symbol): IRegistry<T> {
        /// create box if not exists
        if (this.registryBox.has(sym) === false) {
            this.registryBox.set(sym, new Map<string, any>());
        }
        /// take out the box
        const classRegistry = this.registryBox.get(sym);

        // Create an instance cache
        const instanceCache = new Map<string, Map<string, IRegistryInstance<T>>>();

        return {
            registerClass: (meta?: T) => (ctor: new (...args: any[]) => any) => {
                const entry = { ctor, meta };
                /// register normal case
                classRegistry?.set(ctor.name, entry);

                /// also handle alias
                let alias = meta?.alias;
                if (typeof alias === "string") alias = [alias];
                if (Array.isArray(alias)) {
                    alias.forEach((alias) => {
                        if (typeof alias === "string") classRegistry?.set(alias, entry);
                    });
                }
            },

            getClass(name?: string | T): any {
                if (typeof name === "string") return classRegistry?.get(name) ?? null;
                // Assuming 'name' is the meta object, we need to find a match
                const entries = Array.from(classRegistry?.values() || []);
                if (name == undefined) return entries;
                return entries.filter((entry) => matchMeta(name, entry.meta));
            },

            getInstance(nameOrMeta?: string | T, ...args: any[]): any {
                const generateArgsKey = (args: any[]): string => {
                    return args.length === 0 ? "" : fastJSONStableStringify(args) ?? "";
                };

                if (typeof nameOrMeta === "string") {
                    const name = nameOrMeta;
                    const entry = classRegistry?.get(name);
                    if (!entry) return null;

                    const argsKey = generateArgsKey(args);

                    if (!instanceCache.has(name)) {
                        instanceCache.set(name, new Map());
                    }
                    const classInstanceCache = instanceCache.get(name)!;

                    if (classInstanceCache.has(argsKey)) {
                        return classInstanceCache.get(argsKey);
                    } else {
                        const instance = new entry.ctor(...args);
                        const registryInstance: IRegistryInstance<T> = {
                            instance,
                            ...entry,
                        };
                        classInstanceCache.set(argsKey, registryInstance);
                        return registryInstance;
                    }
                } else {
                    const meta = nameOrMeta;
                    const entries = Array.from(classRegistry?.values() || []);
                    if (meta == undefined) return entries;
                    const matchingEntries = entries.filter((entry) =>
                        matchMeta(meta, entry.meta),
                    );
                    if (matchingEntries.length === 0) return null;

                    const results: IRegistryInstance<T>[] = [];

                    matchingEntries.forEach((entry) => {
                        const name = entry.ctor.name;
                        const argsKey = generateArgsKey(args);

                        if (!instanceCache.has(name)) {
                            instanceCache.set(name, new Map());
                        }
                        const classInstanceCache = instanceCache.get(name)!;

                        if (classInstanceCache.has(argsKey)) {
                            results.push(classInstanceCache.get(argsKey)!);
                        } else {
                            const instance = new entry.ctor(...args);
                            const registryInstance: IRegistryInstance<T> = {
                                instance,
                                ...entry,
                            };
                            classInstanceCache.set(argsKey, registryInstance);
                            results.push(registryInstance);
                        }
                    });

                    return results;
                }
            },
        };
    }
}

/// private helpers
function matchMeta(meta1: any, meta2: any): boolean {
    // Check if either meta1 or meta2 is undefined
    if (!meta1 || !meta2) {
        return false;
    }

    for (const key in meta1) {
        if (Object.prototype.hasOwnProperty.call(meta1, key)) {
            if (!meta2.hasOwnProperty(key) || meta1[key] !== meta2[key]) {
                return false;
            }
        }
    }
    return true;
}
